import React from "react";
import { SectionCard, DemandBadge, StatusPill, Empty } from "../../../components/founder/ui";
import api from "../../../lib/api";
import { toast } from "sonner";

function Field({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-lr-leather">{label}</div>
      <div className="text-lr-charcoal mt-0.5">{value || "Not recorded"}</div>
    </div>
  );
}

export default function OverviewTab({ ws, bid, reload }) {
  const b = ws.business;
  const brief = ws.brief;
  const constraintCandidates = ws.constraints.filter((c) => c.status === "Candidate");
  const openContradictions = ws.contradictions.filter((c) => c.status !== "Resolved");

  const beginInvestigation = async () => {
    try {
      await api.patch(`/businesses/${bid}/status`, { status: "Investigation", note: "Investigation started" });
      toast.success("Investigation started");
      reload();
    } catch { toast.error("Could not start investigation"); }
  };

  const answers = ws.intake?.answers || {};
  const qmap = Object.fromEntries((ws.questions?.questions || []).map((q) => [q.id, q]));
  const lbl = (qid) => {
    const q = qmap[qid];
    const v = answers[qid];
    if (v == null || v === "" || (Array.isArray(v) && v.length === 0)) return null;
    const opts = Object.fromEntries((q?.options || []).map((o) => [o.value, o.label]));
    return Array.isArray(v) ? v.map((x) => opts[x] || x).join(", ") : (opts[v] || v);
  };

  let nextAction = "Add a note or begin investigation.";
  if (b.status === "Intake Complete") nextAction = "Begin Investigation";
  else if (b.status === "Investigation") nextAction = openContradictions.length ? "Resolve Contradictions" : "Review Evidence";
  else if (b.status === "Founder Review") nextAction = "Review Blueprint";
  else if (["Intake Sent", "Intake In Progress"].includes(b.status)) nextAction = "Awaiting client intake";

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <SectionCard title="Business Snapshot" testid="overview-snapshot">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Industry" value={b.industry} />
          <Field label="Location" value={b.location} />
          <Field label="Owner" value={b.owner_name} />
          <Field label="Team" value={b.team_size} />
          <Field label="Years operating" value={b.years_operating} />
          <div>
            <div className="text-xs uppercase tracking-wide text-lr-leather">Current stage</div>
            <div className="mt-1"><StatusPill status={b.status} /></div>
          </div>
        </div>
      </SectionCard>

      <SectionCard title="Contact & Online Presence" testid="overview-contact">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Primary contact" value={b.owner_name} />
          <Field label="Email" value={b.email || lbl("contact_email")} />
          <Field label="Phone" value={b.phone || lbl("contact_phone")} />
          <Field label="Preferred contact method" value={lbl("preferred_contact") || b.preferred_contact} />
          <Field label="Website" value={b.website || answers.website} />
          <Field label="Google Business Profile" value={lbl("has_gbp")} />
          <Field label="Google business name" value={b.gbp_name || answers.gbp_name} />
          <Field label="Alternate listing name" value={answers.alt_listing_name} />
        </div>
        <div className="mt-4">
          <div className="text-xs uppercase tracking-wide text-lr-leather">Other online profiles</div>
          <div className="text-lr-charcoal mt-0.5">{[lbl("online_profiles"), lbl("online_profiles_other")].filter(Boolean).join(" · ") || "Not recorded"}</div>
        </div>
        <p className="text-xs text-lr-leather mt-3">Client-reported. Confirm independently and record any findings in Evidence.</p>
      </SectionCard>

      <SectionCard title="Owner Wants" testid="overview-owner-wants">
        <p className="text-lr-charcoal leading-relaxed">{b.owner_objective || brief?.owner_objective || "Not yet captured."}</p>
        {brief?.outcomes_that_matter?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {brief.outcomes_that_matter.map((o) => <span key={o} className="text-xs px-2 py-1 rounded-[3px] bg-lr-clay/12 text-lr-clay">{o}</span>)}
          </div>
        )}
      </SectionCard>

      <SectionCard title="Owner Says the Main Problem Is" testid="overview-main-problem">
        {brief?.owner_stated_biggest_problem ? (
          <blockquote className="border-l-2 border-lr-clay pl-4 text-lr-charcoal italic leading-relaxed">
            "{brief.owner_stated_biggest_problem}"
            <span className="block not-italic text-xs text-lr-leather mt-2">CLIENT_REPORTED</span>
          </blockquote>
        ) : <Empty>No assessment submitted yet.</Empty>}
      </SectionCard>

      <SectionCard title="Preliminary Demand Safety" testid="overview-demand-safety">
        <DemandBadge value={b.preliminary_demand_safety || brief?.preliminary_demand_safety} />
        <p className="text-sm text-lr-leather mt-3">Preliminary signal only. Founder approval determines the final state.</p>
      </SectionCard>

      <SectionCard title="Leading Constraint Candidates" testid="overview-constraints">
        {constraintCandidates.length === 0 && (!brief?.known_client_reported_constraints?.length) ? (
          <Empty>No candidates yet.</Empty>
        ) : (
          <ul className="space-y-2">
            {constraintCandidates.map((c) => (
              <li key={c.id} className="text-lr-charcoal flex items-center justify-between">
                <span>{c.name}</span><span className="text-xs text-lr-clay">candidate</span>
              </li>
            ))}
            {brief?.known_client_reported_constraints?.map((c, i) => (
              <li key={`b${i}`} className="text-lr-charcoal text-sm flex items-center justify-between">
                <span>{c}</span><span className="text-xs text-lr-leather">client-reported</span>
              </li>
            ))}
          </ul>
        )}
        <p className="text-xs text-lr-leather mt-3">Candidates, not confirmed conclusions.</p>
      </SectionCard>

      <SectionCard title="Contradictions" testid="overview-contradictions">
        {openContradictions.length === 0 ? <Empty>None flagged.</Empty> : (
          <ul className="space-y-2">
            {openContradictions.map((c) => <li key={c.id} className="text-lr-charcoal text-sm">• {c.name}</li>)}
          </ul>
        )}
      </SectionCard>

      <SectionCard title="Next Action" testid="overview-next-action">
        {b.status === "Intake Complete" ? (
          <button onClick={beginInvestigation} data-testid="begin-investigation"
            className="inline-flex items-center px-4 py-2 rounded-[4px] bg-lr-olive text-lr-cream font-medium hover:bg-[#3d4230] transition-colors">
            Begin Investigation
          </button>
        ) : (
          <div className="inline-flex items-center px-4 py-2 rounded-[4px] bg-lr-olive text-lr-cream font-medium">{nextAction}</div>
        )}
      </SectionCard>
    </div>
  );
}
